import { useEffect, useRef, useState } from 'react';
import { CalendarHeart, Download, Plus, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { datadogRum } from '@datadog/browser-rum';
import { Catalog } from '../../api/CataloguesAPI';
import { catalogBookingUrl } from '../../shared/catalogLookbook';

const CTA_SENTINEL = 'data-catalog-cta';
const filledCtaClass =
  'inline-flex h-12 items-center justify-center gap-2 rounded-full bg-as-pink-300 px-6 text-sm font-semibold text-white btn-hover-motion hover:bg-as-pink-500';

const openCatalogBooking = (catalog: Catalog) => {
  if (!catalog.bookingLink) {
    return;
  }
  datadogRum.addAction('Catalog book fitting clicked');
  window.open(catalogBookingUrl(catalog.bookingLink), '_blank', 'noopener,noreferrer');
};

type CatalogBookLinkProps = {
  catalog: Catalog;
  fromItem?: boolean;
  className?: string;
  onClick?: () => void;
};

const CatalogBookLink = ({ catalog, fromItem = false, className = '', onClick }: CatalogBookLinkProps) => {
  const { t } = useTranslation();

  return (
    <a
      href={catalogBookingUrl(catalog.bookingLink!)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        event.preventDefault();
        openCatalogBooking(catalog);
        onClick?.();
      }}
      className={`${filledCtaClass} ${className}`}
    >
      <CalendarHeart className="h-4 w-4" aria-hidden />
      {fromItem
        ? t('marketplace.catalogues.lookbook.book_this_look')
        : t('marketplace.catalogues.lookbook.book_fitting')}
    </a>
  );
};

type CatalogImportButtonProps = {
  onImport: () => void;
  className?: string;
};

const CatalogImportButton = ({ onImport, className = '' }: CatalogImportButtonProps) => {
  const { t } = useTranslation();

  return (
    <button type="button" onClick={onImport} className={`${filledCtaClass} ${className}`}>
      <Download className="h-4 w-4" aria-hidden />
      {t('marketplace.catalogues.lookbook.import')}
    </button>
  );
};

type PublicCatalogCtasProps = {
  catalog: Catalog;
  onImport: () => void;
  fromItem?: boolean;
  layout?: 'inline' | 'stack';
};

export const PublicCatalogCtas = ({
  catalog,
  onImport,
  fromItem = false,
  layout = 'inline',
}: PublicCatalogCtasProps) => {
  const showBook = Boolean(catalog.bookingLink);
  const showImport = Boolean(catalog.acceptsB2BOrders);

  if (!showBook && !showImport) {
    return null;
  }

  const wrapperClass =
    layout === 'stack'
      ? 'flex w-full flex-col gap-3'
      : 'flex flex-wrap items-center justify-center gap-3';

  return (
    <div className={wrapperClass} data-catalog-cta="">
      {showBook && <CatalogBookLink catalog={catalog} fromItem={fromItem} />}
      {showImport && <CatalogImportButton onImport={onImport} />}
    </div>
  );
};

type PublicCatalogActionFabProps = {
  catalog: Catalog;
  onImport: () => void;
  fromItem?: boolean;
};

export const PublicCatalogActionFab = ({
  catalog,
  onImport,
  fromItem = false,
}: PublicCatalogActionFabProps) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [showFab, setShowFab] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const showBook = Boolean(catalog.bookingLink);
  const showImport = Boolean(catalog.acceptsB2BOrders);
  const canShow = showBook || showImport;
  const bothActions = showBook && showImport;

  useEffect(() => {
    if (!canShow || typeof IntersectionObserver === 'undefined') {
      setShowFab(false);
      return;
    }

    const intersecting = new Set<Element>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            intersecting.add(entry.target);
          } else {
            intersecting.delete(entry.target);
          }
        });
        setShowFab(intersecting.size === 0);
      },
      { threshold: 0 }
    );

    const observeSentinels = () => {
      observer.disconnect();
      intersecting.clear();
      const nodes = document.querySelectorAll(`[${CTA_SENTINEL}]`);
      if (nodes.length === 0) {
        setShowFab(true);
        return;
      }
      nodes.forEach((node) => observer.observe(node));
    };

    const isCtaSentinel = (node: Node) =>
      node instanceof Element &&
      (node.hasAttribute(CTA_SENTINEL) || Boolean(node.querySelector(`[${CTA_SENTINEL}]`)));

    observeSentinels();

    const mutationObserver = new MutationObserver((mutations) => {
      const shouldResync = mutations.some(
        (mutation) =>
          Array.from(mutation.addedNodes).some(isCtaSentinel) ||
          Array.from(mutation.removedNodes).some(isCtaSentinel)
      );
      if (shouldResync) {
        observeSentinels();
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [canShow]);

  useEffect(() => {
    if (!showFab) {
      setMenuOpen(false);
    }
  }, [showFab]);

  useEffect(() => {
    if (!menuOpen) {
      return;
    }

    const closeOnPointer = (event: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', closeOnPointer);
    document.addEventListener('touchstart', closeOnPointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnPointer);
      document.removeEventListener('touchstart', closeOnPointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen]);

  if (!canShow || !showFab) {
    return null;
  }

  const closeMenu = () => setMenuOpen(false);
  const handleImport = () => {
    closeMenu();
    onImport();
  };

  const renderActionPills = () => (
    <>
      {showBook && (
        <CatalogBookLink catalog={catalog} fromItem={fromItem} onClick={closeMenu} className="shadow-lg" />
      )}
      {showImport && <CatalogImportButton onImport={handleImport} className="shadow-lg" />}
    </>
  );

  return (
    <div
      ref={containerRef}
      data-catalog-action-fab=""
      className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3"
    >
      <div className="hidden flex-col items-end gap-3 md:flex">{renderActionPills()}</div>

      {!bothActions && <div className="md:hidden">{renderActionPills()}</div>}

      {bothActions && (
        <div className="flex flex-col items-end gap-3 md:hidden">
          {menuOpen && renderActionPills()}
          <button
            type="button"
            aria-expanded={menuOpen}
            aria-label={t('marketplace.catalogues.lookbook.more_actions')!}
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-as-pink-300 text-white shadow-lg btn-hover-motion hover:bg-as-pink-500"
          >
            {menuOpen ? <X className="h-6 w-6" aria-hidden /> : <Plus className="h-6 w-6" aria-hidden />}
          </button>
        </div>
      )}
    </div>
  );
};
